import { Injectable } from '@angular/core';

import { Observable, Subject } from 'rxjs';

import { Alert } from '../../models/alert.model';
import { AlertEnum, DialogEnum } from './messages.enum';
import { Info } from '../../models/info.model';
import { Dialog } from '../../models/dialog.model';
import { DialogResponse } from '../../models/dialog-response.model';
import { DialogOption } from '../../models/dialog-option.model';
import { Language } from '../../models/language.model';
import { SelectableOption } from './option-selector-dialog/option-selector-dialog.component';

@Injectable()
export class MessageService {
  private alertSubject = new Subject<any>();
  private timerAlertSubject = new Subject<any>();
  private infoSubject = new Subject<any>();
  private dialogSubject = new Subject<any>();
  private optionSelectorSubject = new Subject<any>();
  private id: number = 0;

  private dialogResponse: any;
  private timerAlertResponse: any;
  private optionSelectorResponse: any;

  constructor() {}

  //-- Alerta --
  startAlert(): Observable<any> {
    return this.alertSubject.asObservable();
  }

  alert(type: AlertEnum, description: string, remove = true) {
    this.id++;
    this.alertSubject.next(<Alert>{ type: type, description: description, alertId: this.id, remove: remove, icon: '' });
  }

  stopAlert(stoped: boolean) {
    // Compatibilidad histórica: este método en realidad cerraba el TimerAlert.
    this.stopTimerAlert(stoped);
  }

  //-- Alerta Timer --
  startTimerAlert(): Observable<any> {
    return this.timerAlertSubject.asObservable();
  }

  /**
   * Muestra una alerta con cuenta regresiva.
   * @param type tipo de alerta
   * @param description descripción base (sin el contador)
   * @param timeSeconds segundos de cuenta regresiva (default 60)
   * @returns Promise<boolean | undefined>:
   * - true      => el usuario hizo clic en el botón de acción (ej. "Continuar sesión")
   * - false     => la cuenta regresiva llegó a 0
   * - undefined => la alerta se cerró por actividad/focus sin que el usuario eligiera (no llamar resume-session)
   */
  timerAlert(
    type: AlertEnum,
    description: string,
    timeSeconds: number = 60,
    options?: { actionLabel?: string; hideClose?: boolean },
  ): Promise<boolean | undefined> {
    // Si ya existía una alerta activa, resolverla como "dismissed"
    try {
      this.timerAlertResponse?.resolve(undefined);
    } catch {
      // ignore
    }

    this.timerAlertResponse = new DialogResponse<boolean | undefined>();
    this.id++;

    this.timerAlertSubject.next(<Alert>{
      type: type,
      description: description,
      alertId: this.id,
      timer: timeSeconds,
      remove: false,
      icon: '',
      actionLabel: options?.actionLabel,
      hideClose: options?.hideClose,
    });

    return this.timerAlertResponse.promise;
  }

  /**
   * Cierra la alerta con timer actual (si existe) y resuelve su promesa.
   * @param stopped true = usuario eligió la acción (ej. "Continuar sesión"), false = cuenta regresiva llegó a 0
   */
  stopTimerAlert(stopped: boolean) {
    try {
      this.timerAlertResponse?.resolve(stopped);
    } catch {
      // ignore
    }
    // Emitir null para que el componente limpie su UI
    this.timerAlertSubject.next(null);
  }

  /**
   * Cierra la alerta con timer porque hubo actividad/focus (ej. usuario volvió a la ventana).
   * Resuelve con undefined para que el consumidor NO llame a resume-session ni onExpired.
   */
  dismissTimerAlert() {
    try {
      this.timerAlertResponse?.resolve(undefined);
    } catch {
      // ignore
    }
    this.timerAlertSubject.next(null);
  }

  //-- Mensaje Información --
  startInfoMessage() {
    return this.infoSubject.asObservable();
  }

  message(info: Info) {
    this.infoSubject.next(info);
  }

  //-- Dialogos --
  startDialog() {
    return this.dialogSubject.asObservable();
  }

  dialog(
    type: DialogEnum,
    title: string,
    message: string,
    option: Array<DialogOption>,
    extra?: { useHtmlMessage?: boolean; equalButtonWidth?: boolean; stackButtons?: boolean },
  ) {
    this.dialogResponse = new DialogResponse<boolean>();

    this.dialogSubject.next(
      new Dialog({
        type,
        title,
        message,
        options: option,
        useHtmlMessage: extra?.useHtmlMessage,
        equalButtonWidth: extra?.equalButtonWidth,
        stackButtons: extra?.stackButtons,
      }),
    );

    return this.dialogResponse.promise;
  }

  sendDialogResponse(response: any) {
    this.dialogResponse?.resolve(response);
  }

  //-- Diálogo Selector de Opciones --
  startOptionSelectorDialog() {
    return this.optionSelectorSubject.asObservable();
  }

  optionSelectorDialog(
    type: DialogEnum,
    title: string,
    message: string,
    options: SelectableOption[],
    showCancelButton: boolean = true,
    defaultColor?: string,
  ) {
    this.optionSelectorResponse = new DialogResponse<number | null>();

    this.optionSelectorSubject.next({
      dialog: new Dialog({ type: type, title: title, message: message, options: [] }),
      options: options,
      showCancelButton: showCancelButton,
      defaultColor: defaultColor,
    });

    return this.optionSelectorResponse.promise;
  }

  sendOptionSelectorResponse(optionId: number | null) {
    this.optionSelectorResponse?.resolve(optionId);
  }
}

